import { useState, useCallback } from 'react';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  image: string;
  merchant: string;
  spec: string;
  quantity: number;
  category: string;
}

const CART_KEY = 'jinbang_cart';

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
}

export function useCart() {
  const [items, setItems] = useState<CartItem[]>(loadCart);

  const addItem = useCallback((item: Omit<CartItem, 'quantity'>) => {
    setItems(prev => {
      const key = `${item.id}-${item.spec}`;
      const exists = prev.find(i => `${i.id}-${i.spec}` === key);
      let next: CartItem[];
      if (exists) {
        next = prev.map(i => `${i.id}-${i.spec}` === key ? { ...i, quantity: i.quantity + 1 } : i);
      } else {
        next = [...prev, { ...item, quantity: 1 }];
      }
      saveCart(next);
      return next;
    });
  }, []);

  const removeItem = useCallback((id: string, spec: string) => {
    setItems(prev => {
      const next = prev.filter(i => !(i.id === id && i.spec === spec));
      saveCart(next);
      return next;
    });
  }, []);

  const updateQty = useCallback((id: string, spec: string, qty: number) => {
    setItems(prev => {
      const next = qty <= 0
        ? prev.filter(i => !(i.id === id && i.spec === spec))
        : prev.map(i => i.id === id && i.spec === spec ? { ...i, quantity: qty } : i);
      saveCart(next);
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem(CART_KEY);
  }, []);

  const totalCount = items.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = items.reduce((s, i) => s + i.price * i.quantity, 0);

  return { items, addItem, removeItem, updateQty, clearCart, totalCount, totalPrice };
}
